// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import {
  type HceSvFilter,
  type SvFilterFlaggedSV,
  type SvFilterIntervalDepth,
  type SvFilterMasked,
  type SvFilterMatchAnnotation,
  type SvFilterPhysicalPosition,
  type SvFilterQualityScore,
  type SvFilterSvBreakpointMoleculeCheck,
  type SvFilterSvLength,
  type SvFilterSvReason,
  type SvFilterSvsBelowPresenceControlDb,
  type SvFilterSVsWithRefGenesDetected,
  type SvFilterSvType,
  type SvFilterZygosity,
  type SvFilterBestSVperCluster
} from '../../../../interfaces/hce/hce-sv-filter';
import {
  DEFAULT_POSITION_MIN,
  DEFAULT_POSITION_MAX,
  SCREEN_TYPE_SV,
  SvFilterSVReason as SvFilterSvReasonDef,
  SvFilterSvType as SvFilterSvTypeDef,
  SvFilterZygosity as SvFilterZygosityDef,
} from '../../../constants/hce/hce-sv-filter-constants';

const toNumberOrDefault = <
  F extends number | undefined
>(
  value: unknown,
  fallback?: F
): F extends number ? number : number | undefined => {
  if (
    value == null ||
    (typeof value === 'string' && value.trim() === '')
  ) return fallback as any;
  const n = Number(value);
  return (Number.isFinite(n) ? n : fallback) as any;
};

const toBooleanOrDefault = <
  F extends boolean | undefined
>(
  value: unknown,
  fallback?: F
): F extends boolean ? boolean : boolean | undefined => {
  return (typeof value === 'boolean'
    ? value
    : fallback) as any;
};

const normalizeArray = <T, R>(
  src: T[] | undefined,
  normalize: (v: T) => R | undefined
): R[] | undefined => {
  if (!src) return undefined;
  const normalized = src
    .map(normalize)
    .filter((v): v is R => v !== undefined);
  return normalized.length === 0 ? undefined : normalized;
};

const normalizeOptional = <T, R>(
  src: T | null | undefined,
  normalize: (v: T) => R
): R | undefined => src != null ? normalize(src) : undefined;

export const normalizeHceSvFilter = (
  src: Partial<HceSvFilter>
): HceSvFilter => {
  const result = {
    filterName: src.filterName,
    screenType: src.screenType ?? SCREEN_TYPE_SV,
    physicalPosition: normalizeArray(src.physicalPosition, normalizeSvFilterPhysicalPosition),
    physicalPositionHide: normalizeArray(src.physicalPositionHide, normalizeSvFilterPhysicalPosition),
    svLength: normalizeArray(src.svLength, normalizeSvFilterSvLength),
    svType: normalizeArray(src.svType, normalizeSvFilterSvType),
    zygosity: normalizeArray(src.zygosity, normalizeSvFilterZygosity),
    flaggedSV: normalizeOptional(src.flaggedSV, normalizeSvFilterFlaggedSV),
    geneName: src.geneName,
    svsWithRefGenesDetected: normalizeOptional(src.svsWithRefGenesDetected, normalizeSvFilterSVsWithRefGenesDetected),
    matchAnnotation: normalizeArray(src.matchAnnotation, normalizeSvFilterMatchAnnotation),
    minIntervalDepth: normalizeOptional(src.minIntervalDepth, normalizeSvFilterIntervalDepth),
    medianIntervalDepth: normalizeOptional(src.medianIntervalDepth, normalizeSvFilterIntervalDepth),
    qualityScore: normalizeOptional(src.qualityScore, normalizeSvFilterQualityScore),
    svReason: normalizeArray(src.svReason, normalizeSvFilterSvReason),
    svBreakpointMoleculeCheck: normalizeOptional(src.svBreakpointMoleculeCheck, normalizeSvFilterSvBreakpointMoleculeCheck),
    svsBelowPresenceControlDb: normalizeOptional(src.svsBelowPresenceControlDb, normalizeSvFilterSvsBelowPresenceControlDb),
    masked: normalizeOptional(src.masked, normalizeSvFilterMasked),
    bestSVperCluster: normalizeOptional(src.bestSVperCluster, normalizeSvFilterBestSVperCluster),
  } satisfies HceSvFilter;
  return result;
};

export const normalizeSvFilterPhysicalPosition = (
  src: Partial<SvFilterPhysicalPosition>
): SvFilterPhysicalPosition => {
  const result = {
    chr: src.chr ?? '',
    posStart: toNumberOrDefault(src.posStart, DEFAULT_POSITION_MIN),
    posEnd: toNumberOrDefault(src.posEnd, DEFAULT_POSITION_MAX),
    posBp: toNumberOrDefault(src.posBp, 1),
  } satisfies SvFilterPhysicalPosition;
  return result;
};

export const normalizeSvFilterSvLength = (
  src: Partial<SvFilterSvLength>
): SvFilterSvLength => {
  const result = {
    lenMin: toNumberOrDefault(src.lenMin, DEFAULT_POSITION_MIN),
    lenMax: toNumberOrDefault(src.lenMax, DEFAULT_POSITION_MAX),
    lenMinBp: toNumberOrDefault(src.lenMinBp, 1),
    lenMaxBp: toNumberOrDefault(src.lenMaxBp, 1),
  } satisfies SvFilterSvLength;
  return result;
};

export const normalizeSvFilterSvType = (
  src: unknown
): SvFilterSvType | undefined => {
  const set = new Set(Object.keys(SvFilterSvTypeDef));
  return set.has(src as SvFilterSvType)
    ? (src as SvFilterSvType)
    : undefined;
};

export const normalizeSvFilterZygosity = (
  src: unknown
): SvFilterZygosity | undefined => {
  const set = new Set(Object.keys(SvFilterZygosityDef));
  return set.has(src as SvFilterZygosity)
    ? (src as SvFilterZygosity)
    : undefined;
};

export const normalizeSvFilterFlaggedSV = (
  src: Partial<SvFilterFlaggedSV>
): SvFilterFlaggedSV => {
  const result = {
    includeEmbedded: toBooleanOrDefault(src.includeEmbedded, false),
  } satisfies SvFilterFlaggedSV;
  return result;
};

export const normalizeSvFilterSVsWithRefGenesDetected = (
  src: Partial<SvFilterSVsWithRefGenesDetected>
): SvFilterSVsWithRefGenesDetected => {
  const result = {
    overlapKG: toBooleanOrDefault(src.overlapKG, false),
  } satisfies SvFilterSVsWithRefGenesDetected;
  return result;
};

export const normalizeSvFilterMatchAnnotation = (
  src: Partial<SvFilterMatchAnnotation>
): SvFilterMatchAnnotation | undefined => {
  if (src.annotation == null) return undefined;
  if (src.annotation.length === 0) return undefined;
  const result = {
    annotation: src.annotation,
    threshold: toNumberOrDefault(src.threshold, 0),
    thresholdunit: src.thresholdunit ?? '',
    excludeMatches: toBooleanOrDefault(src.excludeMatches, false),
  } satisfies SvFilterMatchAnnotation;
  return result;
};

export const normalizeSvFilterIntervalDepth = (
  src: Partial<SvFilterIntervalDepth>
): SvFilterIntervalDepth => {
  const result = {
    min: toNumberOrDefault(src.min),
    max: toNumberOrDefault(src.max),
  } satisfies SvFilterIntervalDepth;
  return result;
};

export const normalizeSvFilterQualityScore = (
  src: Partial<SvFilterQualityScore>
): SvFilterQualityScore => {
  const result = {
    min: toNumberOrDefault(src.min),
    max: toNumberOrDefault(src.max),
  } satisfies SvFilterQualityScore;
  return result;
};

export const normalizeSvFilterSvReason = (
  src: unknown
): SvFilterSvReason | undefined => {
  const set = new Set(Object.keys(SvFilterSvReasonDef));
  return set.has(src as SvFilterSvReason)
    ? (src as SvFilterSvReason)
    : undefined;
};

export const normalizeSvFilterSvBreakpointMoleculeCheck = (
  src: Partial<SvFilterSvBreakpointMoleculeCheck>
): SvFilterSvBreakpointMoleculeCheck => {
  const result = {
    pass: toBooleanOrDefault(src.pass, false),
    fail: toBooleanOrDefault(src.fail, false),
  } satisfies SvFilterSvBreakpointMoleculeCheck;
  return result;
};

export const normalizeSvFilterSvsBelowPresenceControlDb = (
  src: Partial<SvFilterSvsBelowPresenceControlDb>
): SvFilterSvsBelowPresenceControlDb => {
  const result = {
    coLoRSdBControlDb: toNumberOrDefault(src.coLoRSdBControlDb),
    gnomADControlDb: toNumberOrDefault(src.gnomADControlDb),
    ontLRSControlDb: toNumberOrDefault(src.ontLRSControlDb),
  } satisfies SvFilterSvsBelowPresenceControlDb;
  return result;
};

export const normalizeSvFilterMasked = (
  src: Partial<SvFilterMasked>
): SvFilterMasked => {
  const result = {
    excludeMasked: toBooleanOrDefault(src.excludeMasked, false),
  } satisfies SvFilterMasked;
  return result;
};

export const normalizeSvFilterBestSVperCluster = (
  src: Partial<SvFilterBestSVperCluster>
): SvFilterBestSVperCluster => {
  const result = {
    showBestSVperCluster: toBooleanOrDefault(src.showBestSVperCluster, false),
  } satisfies SvFilterBestSVperCluster;
  return result;
};
